import React, { useState, useRef, useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';
import { Icons } from './Icons';
import { STATE_OPTIONS, PRESET_CITIES } from '../utils/usStates';
import CityTypeahead from './CityTypeahead';
import './CityTypeahead.css';
import './CompanyCard.css';

const STATUS_OPTIONS_LIST = ['Not Applied', 'Applied', 'Interviewing', 'Offer', 'Rejected'];
const STATUS_COLORS = {
  'Not Applied': 'status-not-applied',
  Applied: 'status-applied',
  Interviewing: 'status-interviewing',
  Offer: 'status-offer',
  Rejected: 'status-rejected'
};
const FLAG_OPTIONS = ['', 'green', 'yellow'];
const DELETE_TIME = 3;

function useCountdownDelete(onDelete, seconds = DELETE_TIME) {
  const [pending, setPending] = useState(false);
  const [countdown, setCountdown] = useState(seconds);
  const timerRef = useRef(null);
  const intervalRef = useRef(null);
  useEffect(() => { return () => { clearTimeout(timerRef.current); clearInterval(intervalRef.current); }; }, []);
  function start() {
    if (pending) return;
    setPending(true); setCountdown(seconds);
    intervalRef.current = setInterval(() => setCountdown(c => { if (c <= 1) { clearInterval(intervalRef.current); return 0; } return c - 1; }), 1000);
    timerRef.current = setTimeout(async () => { clearInterval(intervalRef.current); await onDelete(); setPending(false); }, seconds * 1000);
  }
  function cancel(toast) {
    clearTimeout(timerRef.current); clearInterval(intervalRef.current);
    timerRef.current = null; intervalRef.current = null;
    setPending(false); setCountdown(seconds);
    if (toast) toast('Delete cancelled');
  }
  return { pending, countdown, start, cancel };
}

export default function CompanyCard({
  company, roles, contacts, existingCities,
  onUpdateCompany, onDeleteCompany,
  onAddRole, onUpdateRole, onDeleteRole,
  onAddContact, onUpdateContact, onDeleteContact,
  showDates, locationMode
}) {
  const toast = useToast();
  const [expanded, setExpanded] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [companyName, setCompanyName] = useState(company.name);
  const [companyNotes, setCompanyNotes] = useState(company.notes || '');
  const [showNotes, setShowNotes] = useState(false);
  const [newRoleTitle, setNewRoleTitle] = useState('');
  const [showAddRole, setShowAddRole] = useState(false);
  const nameInputRef = useRef(null);
  const del = useCountdownDelete(() => onDeleteCompany(company.id));
  const currentFlag = company.flag || '';

  useEffect(() => { if (editingName && nameInputRef.current) nameInputRef.current.focus(); }, [editingName]);

  async function saveCompanyName() {
    if (companyName.trim() && companyName !== company.name) { await onUpdateCompany(company.id, { name: companyName.trim() }); toast('Company updated'); }
    setEditingName(false);
  }
  async function saveCompanyNotes() { await onUpdateCompany(company.id, { notes: companyNotes }); toast('Notes saved'); setShowNotes(false); }
  async function handleAddRole(e) {
    e.preventDefault(); if (!newRoleTitle.trim()) return;
    await onAddRole(company.id, newRoleTitle); setNewRoleTitle(''); setShowAddRole(false); toast('Role added');
  }
  async function cycleFlag() {
    const idx = FLAG_OPTIONS.indexOf(currentFlag);
    const next = FLAG_OPTIONS[(idx + 1) % FLAG_OPTIONS.length];
    await onUpdateCompany(company.id, { flag: next });
    if (next) toast(`Flagged ${next}`); else toast('Flag removed');
  }

  if (del.pending) {
    return (
      <div className="company-card company-card-deleting slide-up">
        <div className="cc-undo-bar">
          <span>Deleting "{company.name}" in <strong>{del.countdown}s</strong></span>
          <button className="cc-undo-btn" onClick={() => del.cancel(toast)}>Undo</button>
        </div>
      </div>
    );
  }

  const flagClass = currentFlag ? `company-card-flag-${currentFlag}` : '';

  return (
    <div className={`company-card slide-up ${flagClass}`}>
      <div className="cc-header" onClick={() => !editingName && setExpanded(!expanded)}>
        <div className="cc-expand-icon" style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}><Icons.ChevronRight size={16} /></div>
        {editingName ? (
          <input ref={nameInputRef} className="cc-name-input" value={companyName} onChange={e => setCompanyName(e.target.value)}
            onBlur={saveCompanyName} onKeyDown={e => { if (e.key === 'Enter') saveCompanyName(); if (e.key === 'Escape') { setCompanyName(company.name); setEditingName(false); } }}
            onClick={e => e.stopPropagation()} />
        ) : <h3 className="cc-name">{company.name}</h3>}
        <div className="cc-meta">
          <span className="cc-role-count">{roles.length} role{roles.length !== 1 ? 's' : ''}</span>
          {roles.map(r => <span key={r.id} className={`cc-status-dot ${STATUS_COLORS[r.status]}`} title={r.status} />)}
        </div>
        <div className="cc-actions" onClick={e => e.stopPropagation()}>
          <button className={`cc-action-btn cc-flag-btn ${currentFlag ? `flag-${currentFlag}` : ''}`} onClick={cycleFlag} title={currentFlag ? `Flag: ${currentFlag} (click to cycle)` : 'Add flag'}>
            <Icons.Flag size={14} />
          </button>
          <button className="cc-action-btn" onClick={() => { setShowNotes(!showNotes); if (!expanded) setExpanded(true); }} title="Company notes"><Icons.StickyNote size={14} /></button>
          <button className="cc-action-btn" onClick={() => setEditingName(true)} title="Edit name"><Icons.Edit size={14} /></button>
          <button className="cc-action-btn danger" onClick={del.start} title="Delete company"><Icons.Trash size={14} /></button>
        </div>
      </div>

      {showNotes && expanded && (
        <div className="cc-company-notes">
          <textarea value={companyNotes} onChange={e => setCompanyNotes(e.target.value)} placeholder="Add company notes (visible when shared with notes permission)..." rows={2} />
          <div className="cc-notes-actions">
            <button className="notes-save-btn" onClick={saveCompanyNotes}>Save</button>
            <button className="notes-cancel-btn" onClick={() => { setCompanyNotes(company.notes || ''); setShowNotes(false); }}>Cancel</button>
          </div>
        </div>
      )}
      {expanded && !showNotes && company.notes && (
        <div className="cc-notes-preview" onClick={() => setShowNotes(true)}><Icons.StickyNote size={12} /> <span>{company.notes}</span></div>
      )}

      {expanded && (
        <div className="cc-body">
          {roles.length === 0 && !showAddRole && <div className="cc-empty">No roles yet</div>}
          {roles.map(role => (
            <RoleRow key={role.id} role={role} contacts={contacts.filter(c => c.roleId === role.id)} companyId={company.id}
              onUpdateRole={onUpdateRole} onDeleteRole={onDeleteRole} onAddContact={onAddContact} onUpdateContact={onUpdateContact} onDeleteContact={onDeleteContact}
              showDates={showDates} locationMode={locationMode} existingCities={existingCities} />
          ))}
          {showAddRole ? (
            <form className="add-role-form" onSubmit={handleAddRole}>
              <input autoFocus value={newRoleTitle} onChange={e => setNewRoleTitle(e.target.value)} placeholder="Role title (e.g. Quant Analyst)" maxLength={100} />
              <button type="submit" disabled={!newRoleTitle.trim()}><Icons.Check size={14} /></button>
              <button type="button" onClick={() => { setShowAddRole(false); setNewRoleTitle(''); }}><Icons.X size={14} /></button>
            </form>
          ) : <button className="add-role-btn" onClick={() => setShowAddRole(true)}><Icons.Plus size={14} /> Add Role</button>}
        </div>
      )}
    </div>
  );
}

function RoleRow({ role, contacts, companyId, onUpdateRole, onDeleteRole, onAddContact, onUpdateContact, onDeleteContact, showDates, locationMode, existingCities }) {
  const toast = useToast();
  const [expanded, setExpanded] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [title, setTitle] = useState(role.title);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState(role.notes || '');
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', designation: '', linkedinStatus: 'Pending' });
  const [editingLocation, setEditingLocation] = useState(false);
  const [editingUpdateMsg, setEditingUpdateMsg] = useState(false);
  const [updateMsgDraft, setUpdateMsgDraft] = useState(role.updateMessage || '');
  const del = useCountdownDelete(() => onDeleteRole(role.id));

  const [locState, setLocState] = useState(() => {
    const loc = role.location || '';
    if (!loc) return '';
    if (loc === 'Remote') return 'Remote';
    const parts = loc.split('·').map(p => p.trim());
    return parts[0] || '';
  });
  const [locCity, setLocCity] = useState(() => {
    const loc = role.location || '';
    if (!loc || loc === 'Remote') return '';
    const parts = loc.split('·').map(p => p.trim());
    return parts[1] || '';
  });
  const [manualLocation, setManualLocation] = useState(role.location || '');

  async function saveTitle() { if (title.trim() && title !== role.title) { await onUpdateRole(role.id, { title: title.trim() }); toast('Role updated'); } setEditingTitle(false); }
  async function handleStatusChange(e) { await onUpdateRole(role.id, { status: e.target.value }); toast('Status updated'); }
  async function handleDateChange(e) { await onUpdateRole(role.id, { appliedDate: e.target.value }); }
  async function saveNotes() { await onUpdateRole(role.id, { notes }); toast('Notes saved'); setShowNotes(false); }
  async function handleAddContact(e) { e.preventDefault(); if (!newContact.name.trim()) return; await onAddContact(role.id, companyId, newContact); setNewContact({ name: '', designation: '', linkedinStatus: 'Pending' }); setShowAddContact(false); toast('Contact added'); }

  async function saveLocation() {
    let loc = '';
    if (locationMode === 'manual') { loc = manualLocation.trim(); }
    else {
      if (locState === 'Remote') loc = 'Remote';
      else if (locState && locCity.trim()) loc = `${locState} · ${locCity.trim()}`;
      else if (locState) loc = locState;
    }
    if (loc !== (role.location || '')) { await onUpdateRole(role.id, { location: loc }); toast('Location saved'); }
    setEditingLocation(false);
  }

  async function saveUpdateMsg() {
    if (updateMsgDraft.trim() !== (role.updateMessage || '')) { await onUpdateRole(role.id, { updateMessage: updateMsgDraft.trim() }); toast('Update message saved'); }
    setEditingUpdateMsg(false);
  }

  if (del.pending) {
    return <div className="role-row role-row-deleting"><div className="cc-undo-bar"><span>Deleting "{role.title}" in <strong>{del.countdown}s</strong></span><button className="cc-undo-btn" onClick={() => del.cancel(toast)}>Undo</button></div></div>;
  }

  const displayLocation = role.location || '';

  return (
    <div className="role-row">
      <div className="role-header" onClick={() => setExpanded(!expanded)}>
        <div className="role-expand" style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}><Icons.ChevronRight size={14} /></div>
        {editingTitle ? (
          <input autoFocus className="role-title-input" value={title} onChange={e => setTitle(e.target.value)}
            onBlur={saveTitle} onKeyDown={e => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') { setTitle(role.title); setEditingTitle(false); } }}
            onClick={e => e.stopPropagation()} />
        ) : <span className="role-title">{role.title}</span>}
        <div className="role-meta" onClick={e => e.stopPropagation()}>
          <select className={`role-status-select ${STATUS_COLORS[role.status]}`} value={role.status} onChange={handleStatusChange}>
            {STATUS_OPTIONS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {!editingLocation && (
            <button className={`role-location-btn ${displayLocation ? 'has-location' : ''}`} onClick={() => setEditingLocation(true)} title={displayLocation || 'Add location'}>
              <Icons.MapPin size={12} /> {displayLocation || 'Location'}
            </button>
          )}
          {showDates && <input type="date" className="role-date-input" value={role.appliedDate || ''} onChange={handleDateChange} title="Applied date" />}
          <span className="role-contact-count">{contacts.length} contact{contacts.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="role-actions" onClick={e => e.stopPropagation()}>
          <button className={`cc-action-btn ${role.updateMessage ? 'has-update-msg' : ''}`} onClick={() => { setEditingUpdateMsg(!editingUpdateMsg); setUpdateMsgDraft(role.updateMessage || ''); }} title={role.updateMessage || 'Add update message'}>
            <Icons.MessageCircle size={14} />
          </button>
          <button className="cc-action-btn" onClick={() => setShowNotes(!showNotes)} title="Notes"><Icons.StickyNote size={14} /></button>
          <button className="cc-action-btn" onClick={() => setEditingTitle(true)} title="Edit"><Icons.Edit size={14} /></button>
          <button className="cc-action-btn danger" onClick={del.start} title="Delete"><Icons.Trash size={14} /></button>
        </div>
      </div>

      {editingLocation && (
        <div className="role-location-editor" onClick={e => e.stopPropagation()}>
          {locationMode === 'manual' ? (
            <input autoFocus className="role-location-manual-input" value={manualLocation} onChange={e => setManualLocation(e.target.value)}
              placeholder="Type location (e.g. London, UK)" maxLength={80}
              onKeyDown={e => { if (e.key === 'Enter') saveLocation(); if (e.key === 'Escape') setEditingLocation(false); }} />
          ) : (
            <div className="role-location-fields">
              <select className="role-location-state-select" value={locState} onChange={e => setLocState(e.target.value)}>
                {STATE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
              {locState && locState !== 'Remote' && (
                <CityTypeahead
                  value={locCity}
                  onChange={setLocCity}
                  onSave={saveLocation}
                  onCancel={() => setEditingLocation(false)}
                  presetCities={PRESET_CITIES}
                  userCities={existingCities || []}
                />
              )}
            </div>
          )}
          <div className="role-location-actions">
            <button className="notes-save-btn" onClick={saveLocation}>Save</button>
            <button className="notes-cancel-btn" onClick={() => setEditingLocation(false)}>Cancel</button>
          </div>
        </div>
      )}

      {editingUpdateMsg && (
        <div className="role-update-msg-editor" onClick={e => e.stopPropagation()}>
          <input autoFocus value={updateMsgDraft} onChange={e => setUpdateMsgDraft(e.target.value)}
            placeholder="e.g. Waiting for HR callback, Phone screen next week..."
            maxLength={120} onKeyDown={e => { if (e.key === 'Enter') saveUpdateMsg(); if (e.key === 'Escape') setEditingUpdateMsg(false); }} />
          <div className="role-location-actions">
            <button className="notes-save-btn" onClick={saveUpdateMsg}>Save</button>
            <button className="notes-cancel-btn" onClick={() => setEditingUpdateMsg(false)}>Cancel</button>
            {role.updateMessage && <button className="notes-cancel-btn" onClick={async () => { await onUpdateRole(role.id, { updateMessage: '' }); setUpdateMsgDraft(''); setEditingUpdateMsg(false); toast('Cleared'); }}>Clear</button>}
          </div>
        </div>
      )}

      {!editingUpdateMsg && role.updateMessage && (
        <div className="role-update-msg-preview" onClick={() => { setEditingUpdateMsg(true); setUpdateMsgDraft(role.updateMessage || ''); }}>
          <Icons.MessageCircle size={12} /> <span>{role.updateMessage}</span>
        </div>
      )}

      {showNotes && (
        <div className="role-notes-section">
          <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add private notes about this role..." rows={3} />
          <div className="role-notes-actions">
            <button className="notes-save-btn" onClick={saveNotes}>Save Notes</button>
            <button className="notes-cancel-btn" onClick={() => { setNotes(role.notes || ''); setShowNotes(false); }}>Cancel</button>
          </div>
        </div>
      )}
      {expanded && (
        <div className="contacts-section">
          {contacts.length === 0 && !showAddContact && <div className="cc-empty small">No contacts for this role</div>}
          {contacts.map(contact => <ContactRow key={contact.id} contact={contact} onUpdateContact={onUpdateContact} onDeleteContact={onDeleteContact} />)}
          {showAddContact ? (
            <form className="add-contact-form" onSubmit={handleAddContact}>
              <input autoFocus value={newContact.name} onChange={e => setNewContact({ ...newContact, name: e.target.value })} placeholder="Contact name" />
              <input value={newContact.designation} onChange={e => setNewContact({ ...newContact, designation: e.target.value })} placeholder="Designation" />
              <button type="button" className={`contact-status-toggle ${newContact.linkedinStatus === 'Connected' ? 'connected' : 'pending'}`}
                onClick={() => setNewContact(prev => ({ ...prev, linkedinStatus: prev.linkedinStatus === 'Connected' ? 'Pending' : 'Connected' }))}>
                {newContact.linkedinStatus === 'Connected' ? '✓ Connected' : '⏳ Pending'}
              </button>
              <button type="submit" disabled={!newContact.name.trim()}><Icons.Check size={14} /></button>
              <button type="button" onClick={() => { setShowAddContact(false); setNewContact({ name: '', designation: '', linkedinStatus: 'Pending' }); }}><Icons.X size={14} /></button>
            </form>
          ) : <button className="add-contact-btn" onClick={() => setShowAddContact(true)}><Icons.Plus size={12} /> Add Contact</button>}
        </div>
      )}
    </div>
  );
}

function ContactRow({ contact, onUpdateContact, onDeleteContact }) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(contact.name);
  const [designation, setDesignation] = useState(contact.designation || '');
  const del = useCountdownDelete(() => onDeleteContact(contact.id));

  async function save() { if (name.trim()) { await onUpdateContact(contact.id, { name: name.trim(), designation: designation.trim() }); toast('Contact updated'); } setEditing(false); }
  async function toggleLinkedIn() {
    const newStatus = contact.linkedinStatus === 'Connected' ? 'Pending' : 'Connected';
    await onUpdateContact(contact.id, { linkedinStatus: newStatus });
    toast(newStatus === 'Connected' ? 'Marked connected' : 'Marked pending');
  }

  if (del.pending) {
    return <div className="contact-row contact-row-deleting"><span style={{ fontSize: 12, color: 'var(--gray-500)' }}>Removing "{contact.name}" in <strong>{del.countdown}s</strong></span><button className="cc-undo-btn" onClick={() => del.cancel(toast)}>Undo</button></div>;
  }

  return (
    <div className="contact-row">
      {editing ? (
        <div className="contact-edit-fields">
          <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder="Name" onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }} />
          <input value={designation} onChange={e => setDesignation(e.target.value)} placeholder="Designation" onKeyDown={e => { if (e.key === 'Enter') save(); }} />
          <button onClick={save}><Icons.Check size={12} /></button>
          <button onClick={() => { setName(contact.name); setDesignation(contact.designation || ''); setEditing(false); }}><Icons.X size={12} /></button>
        </div>
      ) : (
        <>
          <div className="contact-info"><span className="contact-name">{contact.name}</span>{contact.designation && <span className="contact-designation">{contact.designation}</span>}</div>
          <div className="contact-actions-row">
            <button className={`contact-connect-btn ${contact.linkedinStatus === 'Connected' ? 'connected' : 'pending'}`}
              onClick={toggleLinkedIn} title={`Click to mark ${contact.linkedinStatus === 'Connected' ? 'pending' : 'connected'}`}>
              {contact.linkedinStatus === 'Connected' ? '✓ Connected' : '⏳ Pending'}
            </button>
            <button className="cc-action-btn sm" onClick={() => setEditing(true)}><Icons.Edit size={12} /></button>
            <button className="cc-action-btn sm danger" onClick={del.start}><Icons.Trash size={12} /></button>
          </div>
        </>
      )}
    </div>
  );
}
